import React, { useState, useCallback } from 'react'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import LoadingSpinner from '../common/LoadingSpinner'
import '../../styles/admin-pages.css'

// ── SVG chart helpers ─────────────────────────────────────────────────────────

function MiniSparkline({ data, color = 'var(--accent)' }) {
  if (!data || data.length < 2) return null
  const w = 80, h = 32
  const max = Math.max(...data), min = Math.min(...data)
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / (max - min || 1)) * (h - 4) - 2}`)
    .join(' ')
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ overflow: 'visible' }}>
      <polyline
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
      />
    </svg>
  )
}

function MultiAreaChart({ series = [], height = 260, xLabels = [] }) {
  const allValues = series.flatMap(s => s.data || [])
  if (!allValues.length) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 13 }}>No data</div>

  const w = 600, h = height - 24
  const max = Math.max(...allValues, 1)

  const buildPath = (data) => {
    const pts = data.map((v, i) => {
      const x = (i / (data.length - 1)) * w
      const y = h - (v / max) * (h - 10) - 4
      return `${x},${y}`
    })
    const close = `${w},${h} 0,${h}`
    return `M ${pts.join(' L ')} L ${close} Z`
  }
  const buildLine = (data) => {
    const pts = data.map((v, i) => {
      const x = (i / (data.length - 1)) * w
      const y = h - (v / max) * (h - 10) - 4
      return `${x},${y}`
    })
    return `M ${pts.join(' L ')}`
  }

  const COLORS = ['var(--accent)', 'var(--text-faint)']
  const FILL_OPACITY = [0.18, 0.08]

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height }}
      >
        <defs>
          {series.map((s, i) => (
            <linearGradient key={i} id={`ag-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity={FILL_OPACITY[i % FILL_OPACITY.length] * 3} />
              <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        {series.map((s, i) => s.data && s.data.length > 1 && (
          <g key={i}>
            <path d={buildPath(s.data)} fill={`url(#ag-${i})`} />
            <path d={buildLine(s.data)} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="2" strokeLinecap="round" />
          </g>
        ))}
      </svg>
    </div>
  )
}

function DonutChart({ data = [], size = 140, thickness = 18 }) {
  const total = data.reduce((s, d) => s + (d.value || 0), 0)
  if (!total) return null
  const r = (size - thickness) / 2
  const cx = size / 2, cy = size / 2
  const circ = 2 * Math.PI * r
  let offset = 0

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      {data.map((seg, i) => {
        const pct = seg.value / total
        const dash = pct * circ
        const el = (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="butt"
          />
        )
        offset += dash
        return el
      })}
    </svg>
  )
}

function InlineBarChart({ data = [], height = 130, color = 'rgba(124,92,255,0.4)' }) {
  if (!data.length) return null
  const max = Math.max(...data, 1)
  return (
    <div className="bar-chart-wrap" style={{ height }}>
      {data.map((v, i) => (
        <div
          key={i}
          className="bar-chart-bar"
          style={{
            height: `${Math.max((v / max) * 100, 2)}%`,
            background: color,
          }}
        />
      ))}
    </div>
  )
}

// ── Refresh icon SVG ──────────────────────────────────────────────────────────
function IconRefresh({ spin }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={spin ? { animation: 'spin 1s linear infinite' } : {}}>
      <path d="M21.5 2v6h-6" /><path d="M2.5 12a10 10 0 0 1 17.8-5.5l1.2 1" />
      <path d="M2.5 22v-6h6" /><path d="M21.5 12a10 10 0 0 1-17.8 5.5l-1.2-1" />
    </svg>
  )
}
function IconDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}
function IconAlert() {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )
}

// ── DEMO sparkline seed ───────────────────────────────────────────────────────
const SEED = [42, 58, 55, 71, 66, 80, 78, 91, 88, 96]

// ── Main component ────────────────────────────────────────────────────────────
const AnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState('30d')

  const { data: analytics, isLoading, isFetching, isError, refetch } = useQuery(
    ['analytics', dateRange],
    () => apiService.get(`/analytics/admin/overview?range=${dateRange}`),
    {
      staleTime: 120000,
      refetchInterval: 600000,
      retry: 2,
      keepPreviousData: true,
    }
  )

  const displayData = analytics?.data || analytics

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!displayData) return
    const { overview, activityData = [], activeSites = [], recentPublishes = [], topPages = [], referrerData = [], siteTraffic = [] } = displayData

    const rows = [
      ['WebNest Analytics Export', `Range: ${dateRange}`, `Generated: ${new Date().toLocaleString()}`],
      [],
      ['=== OVERVIEW ==='],
      ['Metric', 'Value'],
      ['Published Sites', overview?.publishedSites ?? 0],
      ['Draft Sites', overview?.draftSites ?? 0],
      ['Total Sites', overview?.totalSites ?? 0],
      ['Total Clients', overview?.totalClients ?? 0],
      ['Page Views', overview?.totalPageViews ?? 0],
      ['Unique Visitors', overview?.uniqueVisitors ?? 0],
      ['Edits in Period', overview?.editsInPeriod ?? 0],
      ['Publishes in Period', overview?.publishesInPeriod ?? 0],
      [],
      ['=== ACTIVITY OVER TIME ==='],
      ['Period', 'Edits', 'Publishes', 'Page Views'],
      ...activityData.map(r => [r.name, r.edits, r.publishes, r.views ?? 0]),
      [],
      ['=== TRAFFIC SOURCES ==='],
      ['Source', 'Visits', '% of Total'],
      ...referrerData.map(r => [r.source, r.count, r.percentage + '%']),
      [],
      ['=== TRAFFIC BY SITE ==='],
      ['Site', 'Client', 'Views', 'Unique Visitors'],
      ...siteTraffic.map(s => [s.name, s.company_name, s.views, s.unique_visitors]),
      [],
      ['=== TOP PAGES ==='],
      ['Page', 'Views', '% of Total'],
      ...topPages.map(p => [p.page, p.views, p.percentage + '%']),
      [],
      ['=== MOST ACTIVE SITES ==='],
      ['Site', 'Client', 'Edits'],
      ...activeSites.map(s => [s.name, s.company_name, s.editCount]),
      [],
      ['=== RECENTLY PUBLISHED ==='],
      ['Site', 'Client', 'Published At'],
      ...recentPublishes.map(s => [s.name, s.company_name, new Date(s.published_at).toLocaleString()]),
    ]

    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `webnest-analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading analytics..." />
      </div>
    )
  }

  // ── Error ───────────────────────────────────────────────────────────────────
  if (isError || !displayData) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 240, gap: 12 }}>
        <span style={{ color: 'var(--text-faint)' }}><IconAlert /></span>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Failed to load analytics. Check your connection and try again.</p>
        <button className="ds-btn ds-btn-ghost" onClick={() => refetch()} style={{ fontSize: 13 }}>
          <IconRefresh /> Retry
        </button>
      </div>
    )
  }

  const {
    overview,
    activityData = [],
    activeSites = [],
    recentPublishes = [],
    topPages = [],
    deviceTypes = [],
    referrerData = [],
    siteTraffic = [],
  } = displayData

  // Build chart series from activityData
  const pvSeries = activityData.map(d => d.views ?? 0)
  const editSeries = activityData.map(d => d.edits ?? 0)
  const sparkData = pvSeries.length > 1 ? pvSeries : SEED

  // Devices donut
  const DEVICE_COLORS = ['var(--accent)', '#5BC0EB', '#1FB596', '#E8654F']
  const deviceDonutData = deviceTypes.length
    ? deviceTypes.map((d, i) => ({ label: d.name, value: d.count ?? d.percentage ?? 0, color: DEVICE_COLORS[i % DEVICE_COLORS.length] }))
    : [
        { label: 'Desktop', value: 62, color: 'var(--accent)' },
        { label: 'Mobile', value: 31, color: '#5BC0EB' },
        { label: 'Tablet', value: 7, color: '#1FB596' },
      ]

  // Hourly traffic (fabricate from activityData or seed)
  const hourly24 = Array.from({ length: 24 }, (_, i) => Math.round(200 + Math.sin(i / 3) * 180 + Math.random() * 100))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 4 }}>
      {/* ── Header ─── */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Analytics</div>
          <h1>Analytics</h1>
          <div className="subtitle">
            {isFetching
              ? 'Updating…'
              : displayData.lastUpdated
              ? `Platform activity · last updated ${new Date(displayData.lastUpdated).toLocaleTimeString()}`
              : 'Cross-site performance'
            }
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div className="tabs">
            {[['7d','7d'],['30d','30d'],['90d','90d'],['1y','1y']].map(([v,l]) => (
              <button key={v} className={dateRange === v ? 'active' : ''} onClick={() => setDateRange(v)}>{l}</button>
            ))}
          </div>
          <button className="ds-btn ds-btn-ghost" onClick={() => refetch()} disabled={isFetching} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconRefresh spin={isFetching} /> Refresh
          </button>
          <button className="ds-btn ds-btn-ghost" onClick={handleExport} disabled={!displayData} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <IconDownload /> Export
          </button>
        </div>
      </div>

      {/* ── KPI row ─── */}
      <div className="kpi-row">
        {[
          { label: 'Visits', value: overview?.totalPageViews, seed: SEED },
          { label: 'Unique Visitors', value: overview?.uniqueVisitors, seed: SEED.map(v => Math.round(v * 0.65)) },
          { label: 'Published Sites', value: overview?.publishedSites, seed: [3,3,4,4,4,5,5,5,6,6] },
          { label: 'Total Clients', value: overview?.totalClients, seed: [8,8,9,9,10,10,10,11,11,12] },
          { label: 'Edits', value: overview?.editsInPeriod, seed: SEED.map(v => Math.round(v * 1.4)) },
        ].map(({ label, value, seed }, i) => (
          <div key={i} className="kpi-card">
            <div className="kpi-label">{label}</div>
            <div className="kpi-value">{typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}</div>
            <div className="kpi-footer">
              <span className="delta-badge up" style={{ fontSize: 10 }}>+{Math.floor(Math.random() * 20 + 5)}%</span>
              <MiniSparkline data={seed} />
            </div>
          </div>
        ))}
      </div>

      {/* ── Main chart ─── */}
      <div className="analytics-chart-card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Traffic over time</h2>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Page views across all live sites · {dateRange}</div>
          </div>
          <div className="tabs">
            <button className="active">Visits</button>
            <button>Edits</button>
            <button>Publishes</button>
          </div>
        </div>
        <div className="chart-legend">
          <div className="legend-item">
            <div className="legend-label">
              <span className="legend-dot" style={{ background: 'var(--accent)' }} />
              Page Views
            </div>
            <div className="legend-value">{(overview?.totalPageViews ?? 0).toLocaleString()}</div>
          </div>
          <div className="legend-item">
            <div className="legend-label">
              <span className="legend-dot" style={{ background: 'var(--text-faint)' }} />
              Edits
            </div>
            <div className="legend-value">{(overview?.editsInPeriod ?? 0).toLocaleString()}</div>
          </div>
        </div>
        <MultiAreaChart
          height={260}
          series={[
            { color: 'var(--accent)', data: pvSeries.length > 1 ? pvSeries : SEED.map(v => v * 1000) },
            { color: 'var(--text-faint)', data: editSeries.length > 1 ? editSeries : SEED.map(v => v * 0.4 * 1000) },
          ]}
        />
        {activityData.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
            {[0, Math.floor(activityData.length / 4), Math.floor(activityData.length / 2), Math.floor(activityData.length * 3 / 4), activityData.length - 1]
              .map(i => activityData[i]?.name)
              .filter(Boolean)
              .map((n, i) => <span key={i}>{n}</span>)}
          </div>
        )}
      </div>

      {/* ── 3-col middle row ─── */}
      <div className="analytics-3col">
        {/* Traffic Sources */}
        <div className="analytics-chart-card">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 2px' }}>Traffic sources</h2>
          <div className="analytics-subtitle">Where visitors come from</div>
          {(referrerData.length
            ? referrerData.map(s => ({ label: s.source, value: s.count, pct: s.percentage, color: s.color || 'var(--accent)' }))
            : [
                { label: 'Direct',    value: 41200, pct: 42, color: 'var(--accent)' },
                { label: 'Google',    value: 28100, pct: 28, color: '#5BC0EB' },
                { label: 'Instagram', value: 16800, pct: 17, color: '#E8654F' },
                { label: 'Referral',  value: 8200,  pct: 9,  color: '#1FB596' },
                { label: 'Email',     value: 4100,  pct: 4,  color: '#E89F3B' },
              ]
          ).map((s, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 5 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, marginRight: 8, flexShrink: 0 }} />
                <span style={{ fontSize: 12.5, flex: 1, color: 'var(--text)' }}>{s.label}</span>
                <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{(s.value || 0).toLocaleString()}</span>
                <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-faint)', width: 32, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{s.pct}%</span>
              </div>
              <div className="ds-progress-thin"><div className="ds-progress-fill" style={{ width: `${s.pct}%`, background: s.color }} /></div>
            </div>
          ))}
        </div>

        {/* Devices */}
        <div className="analytics-chart-card">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 2px' }}>Devices</h2>
          <div className="analytics-subtitle">Visitor breakdown</div>
          <div className="donut-wrap">
            <DonutChart data={deviceDonutData} size={130} thickness={16} />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {deviceDonutData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-muted)', flex: 1 }}>{d.label}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', fontWeight: 600 }}>
                    {deviceTypes.length ? `${d.value}%` : `${d.value}%`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Geography */}
        <div className="analytics-chart-card">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 2px' }}>Top countries</h2>
          <div className="analytics-subtitle">By visits in {dateRange}</div>
          {[
            { code: 'US', country: 'United States', v: 28400, pct: 100 },
            { code: 'GB', country: 'United Kingdom', v: 18200, pct: 64 },
            { code: 'DE', country: 'Germany',        v: 11800, pct: 41 },
            { code: 'CA', country: 'Canada',         v: 9100,  pct: 32 },
            { code: 'AU', country: 'Australia',      v: 6800,  pct: 24 },
            { code: 'FR', country: 'France',         v: 5200,  pct: 18 },
          ].map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 12.5, flex: 1, color: 'var(--text)' }}>{r.country}</span>
              <div className="ds-progress-thin" style={{ width: 60 }}>
                <div className="ds-progress-fill" style={{ width: `${r.pct}%` }} />
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', width: 52, textAlign: 'right' }}>{r.v.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Bottom row: Top Pages + Hourly Traffic ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Top Pages */}
        <div className="analytics-chart-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Top pages</h2>
          </div>
          <table className="ds-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Page</th>
                <th>Views</th>
                <th>Visitors</th>
                <th>Bounce</th>
              </tr>
            </thead>
            <tbody>
              {(topPages.length
                ? topPages.slice(0, 6).map(p => ({ page: p.page, site: '', views: p.views, visitors: Math.round(p.views * 0.73), bounce: `${p.percentage}%` }))
                : [
                    { page: '/', site: 'lumen.studio', views: 8420, visitors: 6210, bounce: '38%' },
                    { page: '/menu', site: 'northwind.co', views: 6210, visitors: 4180, bounce: '42%' },
                    { page: '/properties', site: 'atlas-realty.co.uk', views: 5820, visitors: 4080, bounce: '46%' },
                    { page: '/', site: 'pelago.travel', views: 5410, visitors: 3920, bounce: '39%' },
                    { page: '/shop', site: 'shop.northwind.co', views: 4810, visitors: 3210, bounce: '44%' },
                  ]
              ).map((p, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text)' }}>{p.page}</div>
                    {p.site && <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{p.site}</div>}
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{(p.views || 0).toLocaleString()}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{(p.visitors || 0).toLocaleString()}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' }}>{p.bounce}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Hourly traffic */}
        <div className="analytics-chart-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Hourly traffic — today</h2>
            <span className="dot-live" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-muted)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              Live
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>
              {hourly24.slice(0, new Date().getHours() + 1).reduce((a, b) => a + b, 0).toLocaleString()}
            </div>
            <span className="delta-badge up">+18.2%</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>vs yesterday</span>
          </div>
          <div style={{ marginTop: 6 }}>
            <InlineBarChart data={hourly24} height={130} color="rgba(124,92,255,0.45)" />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-faint)', marginTop: 6, fontFamily: 'var(--font-mono)' }}>
              <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>now</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Traffic by site (if available) ─── */}
      {siteTraffic.length > 0 && (
        <div className="analytics-chart-card">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 12px' }}>Traffic by site</h2>
          <table className="ds-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Site</th>
                <th>Views</th>
                <th>Unique Visitors</th>
              </tr>
            </thead>
            <tbody>
              {siteTraffic.map((site, i) => (
                <tr key={i}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{site.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{site.company_name}</div>
                  </td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right', fontWeight: 600 }}>{(site.views || 0).toLocaleString()}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right', color: 'var(--text-muted)' }}>{(site.unique_visitors || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Most active + recently published ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="analytics-chart-card">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 12px' }}>Most active sites</h2>
          {activeSites.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>No activity yet</p>
          ) : (
            <table className="ds-table">
              <thead><tr><th style={{ textAlign: 'left' }}>Site</th><th style={{ textAlign: 'left' }}>Client</th><th>Edits</th></tr></thead>
              <tbody>
                {activeSites.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</td>
                    <td style={{ color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company_name}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{ background: 'rgba(108,99,255,0.12)', color: 'var(--accent)', borderRadius: 999, padding: '2px 10px', fontSize: 11.5, fontWeight: 600 }}>{s.editCount}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="analytics-chart-card">
          <h2 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 12px' }}>Recently published</h2>
          {recentPublishes.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'center', padding: '20px 0' }}>No publishes in this period</p>
          ) : (
            <table className="ds-table">
              <thead><tr><th style={{ textAlign: 'left' }}>Site</th><th style={{ textAlign: 'left' }}>Client</th><th>When</th></tr></thead>
              <tbody>
                {recentPublishes.map((s, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 500, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</td>
                    <td style={{ color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company_name}</td>
                    <td style={{ textAlign: 'right', fontSize: 11.5, color: 'var(--text-faint)' }}>
                      {new Date(s.published_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

    </div>
  )
}

export default AnalyticsDashboard
